// Agents Store Module
import { createBaseModule } from './base'
import { agentsApi } from '@/services/api'

// Custom actions for agents
const customActions = {
  // Get conversations for an agent
  async getConversations({ commit }, { agentId, includeEmpty = false }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const conversations = await agentsApi.getConversations(agentId, includeEmpty)
      
      return conversations
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch conversations')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Alias for fetchConversations (matches frontend call)
  async fetchConversations({ dispatch }, { agentId, includeEmpty = false }) {
    return dispatch('getConversations', { agentId, includeEmpty })
  },

  // Chat with an agent via unified chat
  async chat({ commit, dispatch }, { agentId, message, sessionId }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      const response = await agentsApi.chatViaUnified(agentId, { message, session_id: sessionId })
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to send message')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get agent sessions
  async getSessions({ commit }, agentId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      return await agentsApi.getSessions(agentId)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch sessions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get a specific session
  async getSession({ commit }, sessionId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const session = await agentsApi.getSession(sessionId)
      
      return session
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch session')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Delete a session
  async deleteSession({ commit, dispatch }, sessionId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      await agentsApi.deleteSession(sessionId)
      
      dispatch('showSuccess', 'Session deleted successfully', { root: true })
      
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete session')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Diagnose conversations (get stats/diagnostic info)
  async diagnoseConversations({ commit }, agentId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Use conversations/diagnose endpoint if available
      const diagnostics = await agentsApi.customRequest('get', `/api/agents/${agentId}/conversations/diagnose`)
      
      return diagnostics
    } catch (error) {
      // Silently fail for diagnostics - it's not critical
      console.warn('Diagnostics not available:', error.message)
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

// Create and export the agents module
export default createBaseModule('agent', agentsApi, {
  customActions
})
